import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, EventEmitter, inject, input, output, viewChild } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';

import { type ApplicationType as Application, permissions, UserService } from '@evc/platform';
import type { Menu, MenuItemType } from '@evc/web-components';
import { BtnActionEnum, DropdownMenuComponent } from '@evc/web-components';
import { TranslatePipe } from '@shared/services/i18n/i18n.module';

import type { FileItem } from '@app/types/file-item.type';

@Component({
  selector: 'add-button-menu',
  templateUrl: './add-button-menu.component.html',
  styleUrls: ['./add-button-menu.component.scss'],
  imports: [
    DropdownMenuComponent,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    TranslatePipe,
  ],
})
export class AddButtonMenuComponent implements OnChanges, OnInit {
  readonly createFolder = output();
  // TODO create payload types...
  readonly filesUpload = output<{ files: File[]; paths?: string[] }>();
  readonly fileOpened = output<{ file: FileItem; app: Application }>();

  broadcast<T>(action: string, payload: T): void {
    const emitter = this[action] as EventEmitter<T>;
    if (!emitter) throw new Error(`Unknown broadcast key: ${action}`);
    emitter.emit(payload);
  }

  readonly disableUpload = input(false);

  readonly menuSelector = viewChild.required(MatMenuTrigger);
  readonly uploadInput = viewChild.required<{
    nativeElement: HTMLInputElement;
  }>('upload');

  addMenuConfig: Menu = { items: [] };

  readonly #translateService = inject(TranslateService);
  readonly #userService = inject(UserService);

  ngOnInit(): void {
    this.setAddMenuConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disableUpload) {
      this.setAddMenuConfig();
    }
  }

  onFilesSelected(): void {
    const files = Array.from(this.uploadInput().nativeElement.files ?? []);
    if (!files.length) return;

    this.filesUpload.emit({ files });
    this.uploadInput().nativeElement.value = '';
  }

  onUploadFile(): void {
    this.uploadInput().nativeElement.click();
  }

  private setAddMenuConfig(): void {
    const items: MenuItemType[] = [
      {
        key: 'list.menu.newFolder',
        text: this.#translateService.stream('list.menu.newFolder'),
        icon: 'info-fill',
        /** 🤯 this output is tight to the context of this contextual menu (bad practice)
         * so... we listen click of main nav button ON THIS contextual menu - not on the cta...
         */
        action: { type: BtnActionEnum.CALLBACK, callbackOrUrl: () => this.createFolder.emit() },
      },
    ];

    const disableUpload = this.disableUpload();
    if (this.#userService.checkHasPermission(permissions.STORAGE_UPLOAD) && !disableUpload) {
      items.push({
        key: 'list.menu.uploadFile',
        text: this.#translateService.stream('list.menu.uploadFile'),
        icon: 'log-out',
        isDisabled: disableUpload,
        action: { type: BtnActionEnum.CALLBACK, callbackOrUrl: (): void => this.onUploadFile() },
      });
    }

    this.addMenuConfig = { items };
  }
}
